/**
 * §A.7 fase 4 · Las medias anonimizadas, vistas desde dentro.
 *
 * ## Es lo mismo que ve un club, pero de todos los tramos a la vez
 *
 * Un club ve su tramo y su diferencia con la media. Aquí se ven **todos los tramos**,
 * que es otra pregunta: dice en qué tamaño de club funciona el producto y en cuál no,
 * y es lo que se mira antes de decidir a quién vender.
 *
 * ## Ningún tramo con menos de cinco clubes existe
 *
 * Y la regla vive **dentro de la vista** de la base de datos, no en este endpoint:
 * con cuatro, cualquiera de los cuatro puede despejar los datos de los otros tres
 * restando los suyos de la media. Que la vista no los produzca significa que ningún
 * endpoint futuro puede olvidarse de la regla.
 *
 * ## Y «todavía no hay» llega como un `422`, no como una lista vacía
 *
 * `422 not_enough_clubs_for_benchmarks`. Es la regla funcionando, no un fallo, y con
 * la plataforma recién arrancada es **el estado normal** — así que pintarlo como un
 * recuadro rojo de error sería enseñarle a Manuel a ignorar los recuadros rojos.
 *
 * Lo primero que escribí aquí esperaba una lista vacía; el `422` se vio abriendo la
 * pantalla, no leyendo el contrato. El de la comparativa del club tiene la misma
 * forma con otro código, y los dos se leen igual: como un estado.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { cuenta, euros, numero } from '@/shared/ui/formato';

interface Tramo {
  readonly size_band: string;
  readonly clubs: number;
  readonly avg_students: number;
  readonly avg_monthly_cents: number;
  readonly avg_cents_per_student: number;
  readonly avg_attendance_rate: number;
  readonly avg_churn_rate: number;
}

const porcentaje = (v: number) => `${Math.round(v * 100)} %`;

export function Comparativas() {
  const tramos = useQuery({
    queryKey: ['admin', 'comparativas'],
    // No se reintenta: la respuesta no cambia hasta que haya cinco clubes.
    retry: false,
    queryFn: async () => {
      const { data, error } = await api.v1.admin.benchmarks.get();
      if (error) {
        const p = aProblema(error);
        if (p.code === 'not_enough_clubs_for_benchmarks') return [];
        throw new ErrorDeApi(p);
      }
      return data as unknown as Tramo[];
    },
  });

  if (tramos.isPending) {
    return <Cargando filas={4} etiqueta="Cargando las medias" />;
  }

  if (tramos.isError || !tramos.data) {
    return (
      <PanelError
        problema={aProblema(tramos.error)}
        reintentar={() => void tramos.refetch()}
      />
    );
  }

  return (
    <section>
      <h1>Comparativas</h1>

      <p className="tenue">
        Las medias por tramo de tamaño, que es el eje honesto: un club de 30
        alumnos y uno de 300 no se parecen en nada aunque estén en la misma
        calle. <strong>Ningún tramo con menos de cinco clubes existe</strong>, y
        eso lo impide la propia vista de la base de datos.
      </p>

      {tramos.data.length === 0 ? (
        <Vacio
          titulo="Todavía no hay ningún tramo con cinco clubes"
          explicacion="Hasta que los haya no se puede publicar una media sin que se pueda deducir de quién son los datos. Con cuatro clubes, cualquiera de ellos despeja los otros tres restando los suyos."
        />
      ) : (
        <div className="tabla-envoltura">
          <table className="tabla">
            <thead>
              <tr>
                <th>Tramo</th>
                <th className="num">Clubes</th>
                <th className="num">Alumnos</th>
                <th className="num">Al mes</th>
                <th className="num">Por alumno</th>
                <th className="num">Asistencia</th>
                <th className="num">Bajas</th>
              </tr>
            </thead>
            <tbody>
              {tramos.data.map((t) => (
                <tr key={t.size_band}>
                  <td>{t.size_band}</td>
                  <td className="num">{t.clubs}</td>
                  <td className="num">{numero(t.avg_students, 0)}</td>
                  <td className="num">{euros(t.avg_monthly_cents)}</td>
                  <td className="num">{euros(t.avg_cents_per_student)}</td>
                  <td className="num">{porcentaje(t.avg_attendance_rate)}</td>
                  <td className="num">{porcentaje(t.avg_churn_rate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="tenue">
        {tramos.data.length > 0 &&
          `${cuenta(tramos.data.length, 'tramo', 'tramos')} con datos suficientes. `}
        Esto es lo que cada club ve de su propio tramo en su pantalla de
        comparativa, y nunca sabe qué clubes lo componen.
      </p>
    </section>
  );
}
